import { RemoteDepositItem } from '@treasury/domain/remote-deposit-capture';
import { LabeledList } from '@treasury/presentation';
import { checkmarkIcon, circleCloseIcon, clockIcon } from '@treasury/presentation/assets/icons';
import { format } from 'date-fns';
import { type TemplateResult } from 'lit';

export class RemoteDepositItemVm extends RemoteDepositItem {
    public get formattedAmount() {
        const dollarUSLocale = Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        });
        return dollarUSLocale.format(this.amount);
    }

    public get formattedDate() {
        return format(this.date, 'iiii MMMM d, yyyy');
    }

    public get statusColor() {
        return this.getStatusMetadata().color;
    }

    public get statusIcon() {
        return this.getStatusMetadata().statusIcon;
    }

    public getLabeledList(locationName: string) {
        // Location Column comes first
        const columns = ['location'];
        // Now push in any required data field
        if (this.requiredDataFields) {
            this.requiredDataFields.forEach(field => columns.push(field.text ?? ''));
        }
        // Optional Fields come next starting with customer
        columns.push('customer');
        // Now push any optional data fields that are left over
        if (this.optionalDataFields) {
            this.optionalDataFields
                .filter(field => field.value)
                .forEach(field => columns.push(field.text ?? ''));
        }

        const source = {
            location: locationName,
            customer: this.customer ? this.customerName : '',
        };

        const requiredData = this.requiredDataFields?.map(field => ({
            [field.text ?? '']: field.value ?? '',
        }));
        const optionalData = this.optionalDataFields
            ?.filter(field => field.value)
            .map(field => ({
                [field.text ?? '']: field.value ?? '',
            }));
        Object.assign(source, ...requiredData);
        Object.assign(source, ...optionalData);

        return new LabeledList(source, columns as never[]);
    }

    private getStatusMetadata() {
        let statusIcon: TemplateResult<2>;
        let color: string;
        switch (this.dto.statusDescription) {
            case 'Approved':
            case 'Deposited':
            case 'Sent to Processing':
            case 'Partial Deposit':
                statusIcon = checkmarkIcon;
                color = '--content-positive';
                break;
            case 'Rejected':
            case 'Check Decisioning Error':
            case 'Error':
            case 'Needs Attention':
            case 'Needs Rescan':
            case 'Processing Error':
                statusIcon = circleCloseIcon;
                color = '--content-negative';
                break;
            default:
                statusIcon = clockIcon;
                color = '--content-attention';
        }
        return { statusIcon, color };
    }
}
